import React, { useState } from "react";
import { useApplication } from "./ApplicationProvider";
import { usePublicConfig } from "./PublicConfigProvider";
import { YesNoRadio } from "./YesNoRadio";
import { formatIsoDate } from "../config";
import { SPONSOR_POST_NUMBER_RE } from "../validation";
import type { YesNo } from "../types";

// Post-submission sponsor step. Shown when an applicant returns via their magic
// link after submitting (status === "Submitted"). Confirming a sponsor moves the
// status Submitted -> Finalized WITHOUT touching the admin Application Status and
// WITHOUT the [AMENDED] flow — this is a lighter, separate milestone.
export function SponsorPanel() {
  const { data, update, draftToken } = useApplication();
  const cfg = usePublicConfig();

  const sponsorDeadline = formatIsoDate(cfg.sponsorDeadline);

  // Local gate for the Yes/No question. Pre-seed from any saved sponsor name so a
  // partially-filled return visit re-opens the fields.
  const [committed, setCommitted] = useState<YesNo | null>(
    data.sponsor.sponsorPostName.trim() || data.sponsor.sponsorPostNumber.trim() ? "Yes" : null
  );
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string>("");

  if (data.meta.status === "Finalized") {
    return (
      <section className="section-card border-green-200 bg-green-50">
        <h2 className="text-xl font-semibold text-green-900 mb-2">&#10003; Sponsor confirmed — Finalized</h2>
        <p className="text-sm text-green-900">
          Thank you! Your application is now <strong>Finalized</strong>. Your sponsor is recorded as{" "}
          <strong>
            {data.sponsor.sponsorPostName}
            {data.sponsor.sponsorPostNumber ? ` #${data.sponsor.sponsorPostNumber}` : ""}
          </strong>
          {data.sponsor.sponsorConfirmedDate
            ? ` (confirmed ${formatIsoDate(data.sponsor.sponsorConfirmedDate)})`
            : ""}
          .
        </p>
      </section>
    );
  }

  const setSponsor = (mut: (s: typeof data.sponsor) => void) =>
    update((d) => {
      mut(d.sponsor);
    });

  const postNumberValid = SPONSOR_POST_NUMBER_RE.test(data.sponsor.sponsorPostNumber.trim());
  const canConfirm =
    committed === "Yes" && data.sponsor.sponsorPostName.trim().length > 0 && postNumberValid;

  const onConfirm = async () => {
    if (!canConfirm || confirming) return;
    setConfirmError("");
    if (!draftToken) {
      setConfirmError(
        "We can't reach your saved application from this browser. Open the link from your confirmation email and try again."
      );
      return;
    }
    setConfirming(true);
    try {
      // Server records the sponsor, moves Submitted -> Finalized, updates the
      // tracker sponsor columns, and sends the [SPONSOR] admin notice. This is
      // deliberately NOT an amendment: no status reset, no [AMENDED] email.
      const res = await fetch(`/api/drafts/${encodeURIComponent(draftToken)}/sponsor`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sponsorPostName: data.sponsor.sponsorPostName,
          sponsorPostNumber: data.sponsor.sponsorPostNumber,
        }),
      });
      const body = (await res.json().catch(() => ({}))) as { error?: string; status?: string };
      if (!res.ok) {
        setConfirmError(body.error || "Could not confirm the sponsor right now. Please try again shortly.");
        return;
      }
      update((d) => {
        d.sponsor.sponsorConfirmed = true;
        d.sponsor.sponsorConfirmedDate = new Date().toISOString().slice(0, 10);
        d.meta.status = body.status === "Finalized" || body.status === "Submitted" ? body.status : "Finalized";
      });
    } catch {
      setConfirmError("Could not confirm the sponsor right now. Please try again shortly.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <section className="section-card border-program/40">
      <h2 className="text-xl font-semibold mb-2">Add your sponsor</h2>
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-4">
        <p className="text-sm text-amber-900">
          Completing the application is the most important thing in this process. Leaving the sponsor
          blank for now is fine — it may be the last thing you accomplish — but secure and confirm
          your sponsor here by <strong>{sponsorDeadline}</strong>.
        </p>
      </div>

      <YesNoRadio
        name="sponsorCommitted"
        label="Do you have a sponsor Post or Unit committed?"
        value={committed}
        onChange={(v) => setCommitted(v)}
      />

      {committed === "Yes" && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label" htmlFor="sponsorPostName">Sponsoring Post / Unit name</label>
            <input
              id="sponsorPostName"
              className="field-input"
              value={data.sponsor.sponsorPostName}
              onChange={(e) => setSponsor((s) => (s.sponsorPostName = e.target.value))}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="sponsorPostNumber">Post / Unit number</label>
            <input
              id="sponsorPostNumber"
              className="field-input"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={3}
              placeholder="e.g. 24"
              value={data.sponsor.sponsorPostNumber}
              onChange={(e) =>
                setSponsor((s) => (s.sponsorPostNumber = e.target.value.replace(/\D/g, "").slice(0, 3)))
              }
            />
            {data.sponsor.sponsorPostNumber.trim().length > 0 && !postNumberValid && (
              <p className="field-error">Post/Unit number must be a 1–3 digit number.</p>
            )}
          </div>
        </div>
      )}

      {committed === "No" && (
        <p className="mt-4 text-sm text-slate-600">
          No problem — come back to this link any time before {sponsorDeadline} to confirm your
          sponsor and finalize your application.
        </p>
      )}

      {confirmError && <p className="mt-3 text-sm text-red-600">{confirmError}</p>}
      <div className="mt-5 flex justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={!canConfirm || confirming}
          onClick={() => void onConfirm()}
        >
          {confirming ? "Confirming…" : "Confirm sponsor"}
        </button>
      </div>
    </section>
  );
}
